import { EXPERIENCE_IDS } from '@/components/admin/experiences/contracts';
import { useVariableFiche } from '@/components/admin/variables/use-variable-fiche';
import { VariableFiche } from '@/components/admin/variables/variable-fiche';
import { VariablePill } from '@/components/admin/variables/variable-pill';
import type { VariableZone } from '@/lib/variable-pill';
import { paintedPieces, variableOfPillToken } from '@/lib/variable-pill';
import type { PersonaContent } from '@matter_extractor/shared';
import { renderPersonaDirective } from '@matter_extractor/shared';
import type { ReactNode } from 'react';

/**
 * « Ce que l'interviewer recevra » - le bloc persona de §10.3, en lecture seule
 * (§10.2 ; maquette `OngletPersona` ; le Contexte depuis le grill Variables du
 * 2026-09-16, D7, issue #88).
 *
 * §10.2 asks for it in as many words: « il montre, en lecture seule, un panneau
 * "Ce que l'interviewer recevra" : le bloc persona de §10.3 rendu depuis la
 * version résolue, surcouche comprise, puis le Contexte de la page protocole,
 * libellé, pour qu'un seul panneau lise tout ce que l'expérience ajoute ». The
 * overlay is written while looking at what it prolongs; the Contexte is read
 * here so the two texts the experience serves are read TOGETHER, in the order
 * §10.3 serves them.
 *
 * The rendering is `renderPersonaDirective`, which #54 moved into `shared` for
 * this panel: it is pure and total, so the editor plays it with NO server call
 * and the sentence an operator reads here is character for character the one
 * the assembler sends.
 *
 * ## The fixed labels are bold, and the direction has none
 *
 * The mockup: « chaque bloc du persona porte son libellé fixe en gras, sauf la
 * direction, qui n'en a pas dans la spec ». `renderPersonaDirective` joins its
 * blocks with a BLANK LINE and puts the fixed label of each in front of a
 * ` : `, so the split is the rendering's own shape rather than a guess - and it
 * is taken on every block BUT THE FIRST, which is the direction and the one
 * place an operator's own colon would otherwise be mistaken for a label.
 *
 * ## The two texts come with their pills, and the pills with their fiche
 *
 * A token is displayed by the variable's CURRENT name (§8.6), in the same pill
 * as the field above it (D5), and the fiche opens on it as everywhere else
 * (D6): a read-only panel is where somebody checks what « @prénom » will be,
 * so it is the one place the fiche is most read. Each text is described from
 * its own place - the overlay's, the Contexte's - so « Rend ici » says what the
 * interviewer will receive.
 */

export interface ExperiencePersonaDirectiveProps {
  /** The persona resolved at the experience's tag, or null while it is read. */
  persona: PersonaContent | null;
  /** The overlay of §10.2, as stored - tokens included. */
  overlay: string;
  /** The Contexte of §9.5, as stored - tokens included. */
  context: string;
  /** What each token of the overlay IS, and of the Contexte (D5, D6). */
  overlayZone: VariableZone;
  contextZone: VariableZone;
}

/** One block of the directive: its fixed label in bold, then what it says. */
function DirectiveBlock({ block, labelled }: { block: string; labelled: boolean }) {
  const cut = labelled ? block.indexOf(' : ') : -1;
  if (cut === -1) {
    return <p className="whitespace-pre-wrap text-gray-800 text-sm leading-6">{block}</p>;
  }
  return (
    <p className="whitespace-pre-wrap text-gray-800 text-sm leading-6">
      <strong className="font-semibold text-bleu-nuit">{block.slice(0, cut)}</strong>
      {block.slice(cut)}
    </p>
  );
}

/** A served text with its tokens drawn as pills - the overlay, the Contexte. */
function ServedText({ text, zone }: { readonly text: string; readonly zone: VariableZone }) {
  return (
    <p className="whitespace-pre-wrap text-gray-800 text-sm leading-6">
      {paintedPieces(text, zone.describe).map((piece, index) =>
        piece.kind === 'text' ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: a run of literal text has no id, and two identical runs are two distinct places in one string
          <span key={`text-${index}`}>{piece.text}</span>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: the same token may appear twice, and each occurrence is its own pill
          <VariablePill key={`variable-${index}`} paint={piece.paint} className="mx-0.5" />
        ),
      )}
    </p>
  );
}

export function ExperiencePersonaDirective({
  persona,
  overlay,
  context,
  overlayZone,
  contextZone,
}: ExperiencePersonaDirectiveProps) {
  const blocks = persona === null ? [] : renderPersonaDirective(persona).split('\n\n');
  const trimmedOverlay = overlay.trim();
  const trimmedContext = context.trim();
  const fiche = useVariableFiche();

  /*
    Which zone a hovered pill belongs to is read off the DOM and not off the
    token: the two texts may cite the same variable, and only the section the
    pill sits in says what it renders there (D4).
  */
  const hovered = ((): { zone: VariableZone; token: string } | null => {
    if (fiche.anchor === null) return null;
    const section = fiche.anchor.element.closest('[data-served]');
    const zone = section?.getAttribute('data-served') === 'context' ? contextZone : overlayZone;
    return { zone, token: fiche.anchor.token };
  })();
  const variable = hovered === null ? null : variableOfPillToken(hovered.token);
  const standing = hovered === null || variable === null ? null : hovered.zone.describe(variable);

  let served: ReactNode = null;
  if (trimmedOverlay !== '' || trimmedContext !== '') {
    served = (
      <>
        <hr className="border-gray-200" />
        {trimmedOverlay === '' ? null : (
          <div data-served="overlay">
            <ServedText text={trimmedOverlay} zone={overlayZone} />
          </div>
        )}
        {trimmedContext === '' ? null : (
          <div data-served="context" className="space-y-1">
            <p className="font-semibold text-bleu-nuit text-xs uppercase tracking-wide">
              Contexte de la page protocole
            </p>
            <ServedText text={trimmedContext} zone={contextZone} />
          </div>
        )}
      </>
    );
  }

  return (
    <section
      data-testid={EXPERIENCE_IDS.directive}
      className="space-y-3 rounded-md border border-gray-200 bg-gray-50 p-4"
      {...fiche.handlers}
    >
      <div className="space-y-1">
        <h3 className="font-semibold text-base text-bleu-nuit leading-6">
          Ce que l’interviewer recevra
        </h3>
        <p className="text-gray-600 text-xs leading-4">
          Rendu depuis la version résolue du persona, surcouche comprise, puis le Contexte de la
          page protocole. Rien ne se modifie ici.
        </p>
      </div>

      {blocks.length === 0 ? (
        <p className="text-gray-700 text-sm">Rien pour l’instant.</p>
      ) : (
        <div className="space-y-2.5">
          {blocks.map((block, index) => (
            <DirectiveBlock key={block} block={block} labelled={index > 0} />
          ))}
        </div>
      )}

      {served}

      {fiche.anchor !== null && hovered !== null && standing !== null ? (
        <VariableFiche
          standing={standing}
          place={hovered.zone.place}
          experienceId={hovered.zone.experienceId}
          anchor={fiche.anchor.element}
          pinned={fiche.pinned}
          ficheRef={fiche.ficheRef}
        />
      ) : null}
    </section>
  );
}
